/**
 * PromptTemplateList - Admin view for managing named ChartMind Prompt Templates.
 * 
 * Each template is a named collection of Position 3 LLM prompts.
 * Admins can create, edit, delete templates, and view/copy per-template QR/invite links.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { contentCardSx } from '../../../../../styles/standaloneLayoutTokens';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import { QRCodeSVG } from 'qrcode.react';
import { chartmindAdminService } from '../../../../../services/chartmind';

const getTemplateUrl = (code) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/join/t_${code}`;
};

const PromptTemplateList = ({
  adminId,
  templates = [],
  onTemplatesChange,
  onEditTemplate,
}) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(null); // template obj
  const [newTemplateName, setNewTemplateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  const handleCreate = useCallback(async () => {
    if (!newTemplateName.trim()) {
      setError('Please enter a template name.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await chartmindAdminService.createTemplate(adminId, { name: newTemplateName.trim() });
      setShowCreateDialog(false);
      setNewTemplateName('');
      onTemplatesChange?.();
    } catch (err) {
      console.error('[PromptTemplateList] create error:', err);
      setError('Failed to create Patient Encounter Template.');
    } finally {
      setCreating(false);
    }
  }, [adminId, newTemplateName, onTemplatesChange]);

  const handleDelete = useCallback(async (templateId) => {
    setDeleting(true);
    setError('');
    try {
      await chartmindAdminService.deleteTemplate(adminId, templateId);
      setShowDeleteDialog(null);
      onTemplatesChange?.();
    } catch (err) {
      console.error('[PromptTemplateList] delete error:', err);
      setError('Failed to delete Patient Encounter Template.');
    } finally {
      setDeleting(false);
    }
  }, [adminId, onTemplatesChange]);

  const handleCopyLink = useCallback(async (code) => {
    try {
      await navigator.clipboard.writeText(getTemplateUrl(code));
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, []);

  const handleRegenerateCode = useCallback(async (templateId) => {
    try {
      await chartmindAdminService.regenerateTemplateInviteCode(adminId, templateId);
      onTemplatesChange?.();
    } catch (err) {
      console.error('[PromptTemplateList] regenerate error:', err);
      setError('Failed to regenerate invite code.');
    }
  }, [adminId, onTemplatesChange]);

  // Count enabled prompts per template
  const getEnabledCount = (template) => {
    const prompts = template.prompts || {};
    return Object.values(prompts).filter(p => p.enabled && p.prompt).length;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Patient Encounter Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateDialog(true)}
        >
          New Encounter Template
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each Patient Encounter Template is a named collection of Position 3 prompts. Share a template via its QR code or link
        so professional-chartmind users can add it to their list.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {templates.length === 0 ? (
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No Patient Encounter Templates yet. Create your first template to get started.
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ ...contentCardSx }}>
          <List disablePadding>
            {templates.map((template, index) => (
              <React.Fragment key={template.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{ py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => onEditTemplate?.(template)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">{template.name}</Typography>
                        <Chip
                          size="small"
                          label={`${getEnabledCount(template)} prompts`}
                          color={getEnabledCount(template) > 0 ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Code: {template.inviteCode}
                        {template.createdAt?.toDate
                          ? ` | Created: ${template.createdAt.toDate().toLocaleDateString()}`
                          : ''}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={copied === template.inviteCode ? 'Copied!' : 'Copy link'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLink(template.inviteCode);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Show QR Code">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowQRDialog(template);
                        }}
                      >
                        <QrCode2Icon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Prompts">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTemplate?.(template);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteDialog(template);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Prompt Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Template Name"
            placeholder="e.g., Emergency Medicine, Primary Care"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={18} /> : null}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteDialog} onClose={() => setShowDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{showDeleteDialog?.name}</strong>?
            Users who have linked this template will no longer be able to use its prompts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDelete(showDeleteDialog?.id)}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!showQRDialog} onClose={() => setShowQRDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Template QR Code</DialogTitle>
        <DialogContent>
          {showQRDialog && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {showQRDialog.name}
              </Typography>
              <Box sx={{ display: 'inline-block', p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <QRCodeSVG
                  value={getTemplateUrl(showQRDialog.inviteCode)}
                  size={200}
                  level="M"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {getTemplateUrl(showQRDialog.inviteCode)}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyLink(showQRDialog.inviteCode)}
                >
                  {copied === showQRDialog.inviteCode ? 'Copied!' : 'Copy Link'}
                </Button>
                <Tooltip title="Generate a new code (invalidates the old one)">
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      handleRegenerateCode(showQRDialog.id);
                      setShowQRDialog(null);
                    }}
                  >
                    Regenerate
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQRDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptTemplateList;
